import React, { useEffect, useMemo, useState } from "react";
import "../../App.css";
import "./IplMatchesPage.css";
import { getIplClientId } from "../../config/iplClientMap";

export default function IplMatchesPage({ sessionObject }) {
  const IPL_MATCH_CACHE_KEY = "ipl-matches-cache";
  const IPL_MATCHES_URL = "https://saraleway.com:8001/tie_details/ipl";
  const IPL_LIVE_SCORES_URL =
    "https://saraleway.com:8001/tie_details/live-scores";
  const IPL_PREDICTION_URL =
    "https://saraleway.com:8001/tie_details/prediction";
  const IPL_PREDICTION_CHECK_URL =
    "https://saraleway.com:8001/tie_details/prediction/check";
  const IPL_PREDICTION_WINNERS_URL =
    "https://saraleway.com:8001/tie_details/prediction-winners";
  const IPL_LEADERBOARD_URL =
    "https://saraleway.com:8001/tie_details/leaderboard/overall";
  const IPL_USER_HISTORY_URL =
    "https://saraleway.com:8001/tie_details/user-full-history";

  const [iplMatches, setIplMatches] = useState({
    today: [],
    upcoming: [],
  });
  const [loading, setLoading] = useState(true);
  const [predictionState, setPredictionState] = useState({});
  const [liveScores, setLiveScores] = useState({});
  const [winnerDetailsState, setWinnerDetailsState] = useState({});
  const [activeTab, setActiveTab] = useState("today");
  const [leaderboardState, setLeaderboardState] = useState({
    loading: true,
    title: "IPL 2026 Leaderboard",
    topFive: [],
  });
  const [userHistoryState, setUserHistoryState] = useState({
    loading: true,
    stats: null,
    history: [],
  });
  const userId = Number(sessionObject?.sessionVariables?.user_id || 0);
  const userName = sessionObject?.sessionVariables?.user_name;
  const clientId = getIplClientId();

  const teamThemeMap = {
    "Chennai Super Kings": {
      shortName: "CSK",
      accent: "#f5c116",
      deep: "#ffde59",
      ink: "#1a2b5f",
      logo: "/ipl-logos/chennai-super-kings.jpeg",
    },
    "Mumbai Indians": {
      shortName: "MI",
      accent: "#2d6cdf",
      deep: "#173f8a",
      ink: "#ffffff",
      logo: "/ipl-logos/mumbai-indians.jpeg",
    },
    "Royal Challengers Bengaluru": {
      shortName: "RCB",
      accent: "#d2212a",
      deep: "#111111",
      ink: "#ffffff",
      logo: "/ipl-logos/royal-challengers-bengaluru.jpg",
    },
    "Royal Challengers Bangalore": {
      shortName: "RCB",
      accent: "#d2212a",
      deep: "#111111",
      ink: "#ffffff",
      logo: "/ipl-logos/royal-challengers-bengaluru.jpg",
    },
    "Kolkata Knight Riders": {
      shortName: "KKR",
      accent: "#6f42c1",
      deep: "#2e134d",
      ink: "#ffffff",
      logo: "/ipl-logos/kolkata-knight-riders.jpg",
    },
    "Delhi Capitals": {
      shortName: "DC",
      accent: "#1f5bd8",
      deep: "#d62f2f",
      ink: "#ffffff",
      logo: "/ipl-logos/delhi-capitals.jpg",
    },
    "Sunrisers Hyderabad": {
      shortName: "SRH",
      accent: "#ff7a00",
      deep: "#5c2300",
      ink: "#ffffff",
      logo: "/ipl-logos/sunrisers-hyderabad.png",
    },
    "Rajasthan Royals": {
      shortName: "RR",
      accent: "#ff66b3",
      deep: "#17479e",
      ink: "#ffffff",
      logo: "/ipl-logos/rajasthan-royals.png",
    },
    "Punjab Kings": {
      shortName: "PBKS",
      accent: "#d71920",
      deep: "#7c1014",
      ink: "#ffffff",
      logo: "/ipl-logos/punjab-kings.jpg",
    },
    "Lucknow Super Giants": {
      shortName: "LSG",
      accent: "#00a8e8",
      deep: "#ff7a00",
      ink: "#ffffff",
      logo: "/ipl-logos/lucknow-super-giants.jpg",
    },
    "Gujarat Titans": {
      shortName: "GT",
      accent: "#17305c",
      deep: "#3d5a97",
      ink: "#ffffff",
      logo: "/ipl-logos/gujarat-titans.jpg",
    },
  };

  const getTeamTheme = (teamName) => {
    return (
      teamThemeMap[teamName] || {
        shortName: String(teamName || "IPL")
          .split(" ")
          .map((part) => part[0])
          .join("")
          .slice(0, 3)
          .toUpperCase(),
        accent: "#0f7b3b",
        deep: "#17212b",
        ink: "#ffffff",
        logo: "",
      }
    );
  };

  const parseMatchDateTime = (matchDateTime) => {
    if (!matchDateTime) return null;

    const normalizedValue = String(matchDateTime).trim();
    const [datePart = "", timePart = ""] = normalizedValue.split(" ");
    const [day, month, year] = datePart.split("-").map(Number);
    const [hours = 0, minutes = 0] = timePart.split(":").map(Number);

    if (
      !day ||
      !month ||
      !year ||
      Number.isNaN(hours) ||
      Number.isNaN(minutes)
    ) {
      const fallbackDate = new Date(normalizedValue);
      return Number.isNaN(fallbackDate.getTime()) ? null : fallbackDate;
    }

    return new Date(year, month - 1, day, hours, minutes);
  };

  const formatMatchTime = (matchDateTime) => {
    if (!matchDateTime) return "";

    const parsedDate = parseMatchDateTime(matchDateTime);
    if (parsedDate) {
      return parsedDate.toLocaleString("en-IN", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    }

    const parts = String(matchDateTime).split(" ");
    const timePart = parts[1];

    if (!timePart) {
      return matchDateTime;
    }

    const [hoursString, minutes] = timePart.split(":");
    const hours = Number(hoursString);

    if (Number.isNaN(hours) || minutes === undefined) {
      return matchDateTime;
    }

    const suffix = hours >= 12 ? "PM" : "AM";
    const normalizedHours = hours % 12 || 12;

    return `${parts[0]} ${normalizedHours}:${minutes} ${suffix}`;
  };

  const formatDisplayDate = (matchDateTime) => {
    if (!matchDateTime) return "Today";

    const parsedDate = parseMatchDateTime(matchDateTime);
    if (parsedDate) {
      return parsedDate.toLocaleDateString("en-IN", {
        day: "numeric",
        month: "short",
      });
    }

    const [datePart] = String(matchDateTime).split(" ");
    const [day, month, year] = String(datePart).split("-");

    if (!day || !month || !year) {
      return "Today";
    }

    const date = new Date(`${year}-${month}-${day}T00:00:00`);
    if (Number.isNaN(date.getTime())) {
      return "Today";
    }

    return date.toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
    });
  };

  const getPredictionPercentage = (match, teamName) => {
    const percentage = match?.prediction_ratio?.[teamName];

    if (percentage === undefined || percentage === null || percentage === "") {
      return null;
    }

    return String(percentage);
  };

  const getTodayCacheDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  const readCachedMatches = () => {
    if (typeof window === "undefined") {
      return {
        today: [],
        upcoming: [],
      };
    }

    try {
      const cachedValue = window.localStorage.getItem(IPL_MATCH_CACHE_KEY);
      if (!cachedValue) {
        return [];
      }

      const parsed = JSON.parse(cachedValue);
      if (parsed?.date !== getTodayCacheDate()) {
        return {
          today: [],
          upcoming: [],
        };
      }

      if (
        Array.isArray(parsed?.matches?.today) ||
        Array.isArray(parsed?.matches?.upcoming)
      ) {
        return {
          today: Array.isArray(parsed?.matches?.today)
            ? parsed.matches.today
            : [],
          upcoming: Array.isArray(parsed?.matches?.upcoming)
            ? parsed.matches.upcoming
            : [],
        };
      }
    } catch (error) {}

    return {
      today: [],
      upcoming: [],
    };
  };

  const cacheMatches = (matches) => {
    if (
      typeof window === "undefined" ||
      !matches ||
      (typeof matches !== "object" && !Array.isArray(matches))
    ) {
      return;
    }

    try {
      window.localStorage.setItem(
        IPL_MATCH_CACHE_KEY,
        JSON.stringify({
          date: getTodayCacheDate(),
          matches,
        }),
      );
    } catch (error) {}
  };

  const getDummyInsights = (match, index) => {
    const seed = Number(match.id) || index + 1;
    const teamOneConfidence = 52 + ((seed + index) % 9);
    const teamTwoConfidence = 100 - teamOneConfidence;

    return {
      matchLabel: `Match ${String(index + 1).padStart(2, "0")}`,
      confidence: {
        [match.team_1]: teamOneConfidence,
        [match.team_2]: teamTwoConfidence,
      },
    };
  };

  const enhancedMatches = useMemo(() => {
    const today = Array.isArray(iplMatches?.today) ? iplMatches.today : [];
    const upcoming = Array.isArray(iplMatches?.upcoming)
      ? iplMatches.upcoming
      : [];

    return {
      today: today.map((match, index) => ({
        ...match,
        insights: getDummyInsights(match, index),
      })),
      upcoming: upcoming.map((match, index) => ({
        ...match,
        insights: getDummyInsights(match, index),
      })),
    };
  }, [iplMatches]);

  const liveMatchCount = useMemo(() => {
    return Object.values(liveScores).filter((score) => score?.is_live).length;
  }, [liveScores]);

  const filteredMatches = useMemo(() => {
    const selectedMatches =
      activeTab === "upcoming"
        ? enhancedMatches.upcoming
        : enhancedMatches.today;
    const labelPrefix = activeTab === "upcoming" ? "Upcoming" : "Match";

    return selectedMatches.map((match, index) => ({
      ...match,
      id: activeTab === "upcoming" ? `${match.id}-upcoming` : match.id,
      originalId: match.id,
      insights: {
        ...match.insights,
        matchLabel: `${labelPrefix} ${String(index + 1).padStart(2, "0")}`,
      },
    }));
  }, [activeTab, enhancedMatches, predictionState]);

  const summaryCards = useMemo(() => {
    const todayMatches = enhancedMatches.today;
    const upcomingMatches = enhancedMatches.upcoming;
    const selectedMatches =
      activeTab === "upcoming" ? upcomingMatches : todayMatches;
    const nextMatch =
      selectedMatches[0] || todayMatches[0] || upcomingMatches[0];
    const pickedCount = Object.values(predictionState).filter(
      (item) => item?.winnerTeam,
    ).length;
    const liveScoreSnapshot = Object.entries(liveScores).find(
      ([, score]) => score,
    );
    const snapshotMatch =
      [...todayMatches, ...upcomingMatches].find(
        (match) => Number(match.id) === Number(liveScoreSnapshot?.[0]),
      ) || nextMatch;
    const liveScoreData = liveScoreSnapshot?.[1] || null;
    const scoreSummary =
      snapshotMatch && liveScoreData
        ? `${getTeamTheme(snapshotMatch.team_1).shortName} ${
            liveScoreData.team_1_score || "--/--"
          } • ${getTeamTheme(snapshotMatch.team_2).shortName} ${
            liveScoreData.team_2_score || "--/--"
          }`
        : null;
    const scoreOvers = liveScoreData?.overs || "--.-";
    const scoreStatus = liveScoreData?.status || "Score update awaited";

    return [
      {
        label: activeTab === "upcoming" ? "Upcoming Matches" : "Matches Today",
        value: selectedMatches.length || "00",
        detail:
          activeTab === "upcoming"
            ? "Scheduled next fixtures"
            : "Fresh fixture list",
      },
      {
        label: "Live Matches",
        value: String(liveMatchCount).padStart(2, "0"),
        detail: liveMatchCount
          ? "Live score feed active"
          : "No live match right now",
      },
      {
        label: nextMatch ? "Next Start" : "Predictions Made",
        value: nextMatch
          ? formatMatchTime(nextMatch.match_date_time)
              .split(" ")
              .slice(-2)
              .join(" ")
          : String(pickedCount).padStart(2, "0"),
        detail: nextMatch
          ? formatDisplayDate(nextMatch.match_date_time)
          : pickedCount
            ? "Your picks"
            : "Pick your winners",
      },
      {
        label: "Live Score",
        value: scoreSummary || "--",
        detail: scoreSummary ? `Overs ${scoreOvers}` : "Score update awaited",
        status: scoreSummary ? scoreStatus : "",
      },
      {
        type: "prediction",
        label: "Your Predictions",
        match: nextMatch,
        prediction: nextMatch ? predictionState[nextMatch.id] || null : null,
        winnerDetails: nextMatch
          ? winnerDetailsState[nextMatch.id] || null
          : null,
      },
    ];
  }, [
    activeTab,
    enhancedMatches,
    liveMatchCount,
    liveScores,
    predictionState,
    winnerDetailsState,
  ]);

  const tabConfig = [
    { id: "today", label: "Today" },
    { id: "upcoming", label: "Upcoming" },
  ];

  const fetchPredictionStatus = async (matches) => {
    if (
      !userId ||
      !clientId ||
      !Array.isArray(matches) ||
      matches.length === 0
    ) {
      return;
    }

    try {
      const responses = await Promise.all(
        matches.map(async (match) => {
          try {
            const response = await fetch(
              `${IPL_PREDICTION_CHECK_URL}?schedule_id=${match.id}&user_id=${userId}&client_id=${clientId}`,
              {
                method: "GET",
                headers: {
                  accept: "application/json",
                },
              },
            );

            if (!response.ok) {
              return null;
            }

            const result = await response.json();
            if (!result?.data?.is_predicted || !result?.data?.data) {
              return null;
            }

            return {
              matchId: match.id,
              winnerTeam: result.data.data.winner_team,
              message: `Prediction locked for ${result.data.data.winner_team}.`,
            };
          } catch (error) {
            return null;
          }
        }),
      );

      const nextPredictionState = {};
      responses.forEach((item) => {
        if (!item) return;
        nextPredictionState[item.matchId] = {
          status: "locked",
          winnerTeam: item.winnerTeam,
          message: item.message,
        };
      });

      if (Object.keys(nextPredictionState).length > 0) {
        setPredictionState((prev) => ({
          ...prev,
          ...nextPredictionState,
        }));
      }
    } catch (error) {}
  };

  const fetchLeaderboard = async () => {
    try {
      const response = await fetch(IPL_LEADERBOARD_URL, {
        method: "GET",
        headers: {
          accept: "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Leaderboard request failed");
      }

      const result = await response.json();
      setLeaderboardState({
        loading: false,
        title: result?.title || "IPL 2026 Leaderboard",
        topFive: Array.isArray(result?.top_5)
          ? result.top_5
          : Array.isArray(result?.top_10)
            ? result.top_10.slice(0, 5)
            : [],
      });
    } catch (error) {
      setLeaderboardState({
        loading: false,
        title: "IPL 2026 Leaderboard",
        topFive: [],
      });
    }
  };

  const fetchLiveScores = async (matches) => {
    if (!Array.isArray(matches) || matches.length === 0) {
      setLiveScores({});
      return;
    }

    try {
      const responses = await Promise.all(
        matches.map(async (match) => {
          const scheduleId = Number(match?.id);

          if (!scheduleId) {
            return null;
          }

          try {
            const response = await fetch(
              `${IPL_LIVE_SCORES_URL}?schedule_id=${scheduleId}`,
              {
                method: "GET",
                headers: {
                  accept: "application/json",
                },
              },
            );

            if (!response.ok) {
              return null;
            }

            const result = await response.json();
            const [scoreEntry] = Array.isArray(result?.scores)
              ? result.scores
              : [];

            if (!scoreEntry) {
              return {
                scheduleId,
                score: null,
              };
            }

            return {
              scheduleId,
              score: {
                schedule_id: scoreEntry.schedule_id,
                team_1_score: scoreEntry.team_1_score || "0/0",
                team_2_score: scoreEntry.team_2_score || "0/0",
                overs: scoreEntry.overs || "0.0",
                status: scoreEntry.status || "Score update awaited",
                is_live: Boolean(scoreEntry.is_live),
                date: result?.date || "",
              },
            };
          } catch (error) {
            return null;
          }
        }),
      );

      const nextScores = {};
      responses.forEach((item) => {
        if (!item?.scheduleId) {
          return;
        }

        nextScores[item.scheduleId] = item.score;
      });

      setLiveScores(nextScores);
    } catch (error) {
      setLiveScores({});
    }
  };

  const fetchWinnerDetails = async (matches) => {
    if (
      !userId ||
      !clientId ||
      !Array.isArray(matches) ||
      matches.length === 0
    ) {
      return;
    }

    try {
      const responses = await Promise.all(
        matches.map(async (match) => {
          const scheduleId = Number(match?.id);

          if (!scheduleId) {
            return null;
          }

          try {
            const response = await fetch(
              `${IPL_PREDICTION_WINNERS_URL}?schedule_id=${scheduleId}&user_id=${userId}&client_id=${clientId}`,
              {
                method: "GET",
                headers: {
                  accept: "application/json",
                },
              },
            );

            if (!response.ok) {
              return null;
            }

            const result = await response.json();

            return {
              scheduleId,
              details: {
                status: result?.status || "success",
                date: result?.date || "",
                totalRecords: Number(result?.total_records || 0),
                winners: Array.isArray(result?.winners) ? result.winners : [],
              },
            };
          } catch (error) {
            return null;
          }
        }),
      );

      const nextWinnerState = {};
      responses.forEach((item) => {
        if (!item?.scheduleId) {
          return;
        }

        nextWinnerState[item.scheduleId] = item.details;
      });

      if (Object.keys(nextWinnerState).length > 0) {
        setWinnerDetailsState((prev) => ({
          ...prev,
          ...nextWinnerState,
        }));
      }
    } catch (error) {}
  };

  const fetchUserHistory = async () => {
    if (!userId || !clientId) return;

    try {
      const response = await fetch(
        `${IPL_USER_HISTORY_URL}?user_id=${userId}&client_id=${clientId}`,
        {
          method: "GET",
          headers: { accept: "application/json" },
        },
      );

      if (!response.ok) throw new Error("History failed");

      const result = await response.json();

      setUserHistoryState({
        loading: false,
        stats: result?.stats || null,
        history: Array.isArray(result?.history) ? result.history : [],
      });
    } catch (err) {
      setUserHistoryState({
        loading: false,
        stats: null,
        history: [],
      });
    }
  };

  const handlePrediction = async (match, winnerTeam) => {
    if (!userId || !userName) {
      setPredictionState((prev) => ({
        ...prev,
        [match.id]: {
          status: "error",
          message: "User session not available.",
        },
      }));
      return;
    }

    if (predictionState[match.id]?.status === "locked") {
      return;
    }

    setPredictionState((prev) => ({
      ...prev,
      [match.id]: {
        status: "loading",
        winnerTeam,
        message: "Saving prediction...",
      },
    }));

    try {
      const response = await fetch(IPL_PREDICTION_URL, {
        method: "POST",
        headers: {
          accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          schedule_id: match.id,
          winner_team: winnerTeam,
          user_name: userName,
          user_id: Number(userId),
          client_id: clientId,
        }),
      });

      if (!response.ok) {
        throw new Error("Prediction request failed");
      }

      let result = {};
      try {
        result = await response.json();
      } catch (error) {
        result = {};
      }

      setPredictionState((prev) => ({
        ...prev,
        [match.id]: {
          status: "locked",
          winnerTeam,
          message: result?.message || `Prediction locked for ${winnerTeam}.`,
        },
      }));
    } catch (error) {
      setPredictionState((prev) => ({
        ...prev,
        [match.id]: {
          status: "error",
          winnerTeam,
          message: "Prediction could not be saved.",
        },
      }));
    }
  };

  useEffect(() => {
    let isMounted = true;
    let liveScoreInterval = null;

    const fetchIplMatches = async () => {
      try {
        const response = await fetch(IPL_MATCHES_URL, {
          method: "POST",
          headers: {
            accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            paginate: {
              number_of_rows: 0,
              page_number: 0,
            },
            filter_fields: {},
          }),
        });

        const result = await response.json();
        if (isMounted) {
          const apiMatches = {
            today: Array.isArray(result?.ipl?.today) ? result.ipl.today : [],
            upcoming: Array.isArray(result?.ipl?.upcoming)
              ? result.ipl.upcoming
              : [],
          };
          const cachedMatches = readCachedMatches();
          const matches =
            apiMatches.today.length > 0 || apiMatches.upcoming.length > 0
              ? apiMatches
              : cachedMatches;
          const allMatches = [...matches.today, ...matches.upcoming];

          if (apiMatches.today.length > 0 || apiMatches.upcoming.length > 0) {
            cacheMatches(apiMatches);
          }

          setIplMatches(matches);
          fetchPredictionStatus(allMatches);
          fetchWinnerDetails(allMatches);
          fetchLiveScores(matches.today);
          liveScoreInterval = setInterval(
            () => {
              fetchLiveScores(matches.today);
            },
            10 * 60 * 1000,
          );
        }
      } catch (error) {
        if (isMounted) {
          setIplMatches({
            today: [],
            upcoming: [],
          });
          setLiveScores({});
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchIplMatches();
    fetchLeaderboard();
    return () => {
      isMounted = false;
      if (liveScoreInterval) {
        clearInterval(liveScoreInterval);
      }
    };
  }, []);

  useEffect(() => {
    fetchUserHistory();
  }, []);

  return (
    <div className="page-products">
      <div className="ipl-page-shell">
        <div className="ipl-page-header">
          <div className="ipl-page-kicker">Cricket Hub</div>
          <div className="ipl-page-title">
            {activeTab === "upcoming"
              ? "Upcoming IPL Matches"
              : "Today's IPL Matches"}
          </div>
          <div className="ipl-page-subtitle">
            Track fixtures, live scores, leaderboard, and predictions in one
            place.
          </div>
        </div>

        <div className="ipl-page-tab-row">
          {tabConfig.map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={`ipl-page-tab ${activeTab === tab.id ? "active" : ""}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="ipl-page-summary-grid">
          {summaryCards.map((card) => (
            <div className="ipl-page-summary-card" key={card.label}>
              <div className="ipl-page-summary-label">{card.label}</div>
              {card.type === "prediction" ? (
                card.match && card.winnerDetails?.winners?.length ? (
                  (() => {
                    const currentUserWinner = card.winnerDetails?.winners?.find(
                      (winner) =>
                        String(winner?.user_name || "").toLowerCase() ===
                        String(userName || "").toLowerCase(),
                    );
                    const resultLabel = currentUserWinner?.status
                      ? currentUserWinner.status
                      : card.prediction?.winnerTeam
                        ? activeTab === "upcoming"
                          ? "PREDICTED"
                          : "RESULT AWAITED"
                        : "NOT PREDICTED";
                    const resultClass = String(resultLabel)
                      .toLowerCase()
                      .replace(/\s+/g, "-");

                    return (
                      <div className="ipl-page-summary-prediction">
                        <div className="ipl-page-summary-matchup">
                          <div
                            className={`ipl-page-summary-team-chip ${
                              card.prediction?.winnerTeam === card.match.team_1
                                ? "selected"
                                : ""
                            }`}
                          >
                            {card.match.team_1}
                          </div>
                          <div className="ipl-page-summary-vs-chip">VS</div>
                          <div
                            className={`ipl-page-summary-team-chip ${
                              card.prediction?.winnerTeam === card.match.team_2
                                ? "selected"
                                : ""
                            }`}
                          >
                            {card.match.team_2}
                          </div>
                        </div>
                        <div className="ipl-page-summary-picked-line">
                          {card.prediction?.winnerTeam
                            ? `Selected: ${card.prediction.winnerTeam}`
                            : "Selected: Not picked yet"}
                        </div>
                        <div
                          className={`ipl-page-summary-result ${resultClass}`}
                        >
                          {resultLabel}
                        </div>
                      </div>
                    );
                  })()
                ) : null
              ) : (
                <>
                  <div className="ipl-page-summary-value">{card.value}</div>
                  <div className="ipl-page-summary-detail">{card.detail}</div>
                  {card.status ? (
                    <div className="ipl-page-summary-status">{card.status}</div>
                  ) : null}
                </>
              )}
            </div>
          ))}
        </div>

        <div className="ipl-page-content-grid">
          <div className="ipl-page-main-column">
            <div className="ipl-page-list">
              {loading ? (
                <div className="ipl-page-empty">Loading matches...</div>
              ) : filteredMatches.length === 0 ? (
                <div className="ipl-page-empty">
                  {activeTab === "upcoming"
                    ? "No upcoming IPL matches available."
                    : "No IPL matches scheduled for today."}
                </div>
              ) : (
                filteredMatches.map((match) => {
                  const teamOneTheme = getTeamTheme(match.team_1);
                  const teamTwoTheme = getTeamTheme(match.team_2);
                  const matchId = match.originalId || match.id;
                  const currentPrediction = predictionState[matchId] || null;
                  const teamOnePercentage = getPredictionPercentage(
                    match,
                    match.team_1,
                  );
                  const teamTwoPercentage = getPredictionPercentage(
                    match,
                    match.team_2,
                  );

                  return (
                    <div className="ipl-page-card" key={match.id}>
                      <div className="ipl-page-card-topline">
                        <div>
                          <div className="ipl-page-time">
                            {formatMatchTime(match.match_date_time)}
                          </div>
                          <div className="ipl-page-date">
                            {formatDisplayDate(match.match_date_time)}
                          </div>
                        </div>
                        <div className="ipl-page-topline-right">
                          <div className="ipl-page-tag">
                            {activeTab === "upcoming" ? "Upcoming" : "Today"}
                          </div>
                          <div className="ipl-page-match-label">
                            {match.insights.matchLabel}
                          </div>
                        </div>
                      </div>
                      <div className="ipl-page-team-row">
                        <div
                          className="ipl-page-team-panel"
                          style={{
                            "--team-accent": teamOneTheme.accent,
                            "--team-deep": teamOneTheme.deep,
                            "--team-ink": teamOneTheme.ink,
                          }}
                        >
                          {teamOneTheme.logo ? (
                            <img
                              src={teamOneTheme.logo}
                              alt={match.team_1}
                              className="ipl-page-team-logo"
                              onError={(event) => {
                                event.currentTarget.style.display = "none";
                                event.currentTarget.nextSibling.style.display =
                                  "flex";
                              }}
                            />
                          ) : null}
                          <div
                            className="ipl-page-team-fallback"
                            style={{
                              display: teamOneTheme.logo ? "none" : "flex",
                            }}
                          >
                            {teamOneTheme.shortName}
                          </div>
                          <div className="ipl-page-team-name">
                            {match.team_1}
                          </div>
                          <div className="ipl-page-team-action-row">
                            <button
                              type="button"
                              className={`ipl-page-predict-button ${
                                currentPrediction?.winnerTeam === match.team_1
                                  ? "selected"
                                  : ""
                              }`}
                              onClick={() =>
                                handlePrediction(
                                  {
                                    ...match,
                                    id: match.originalId || match.id,
                                  },
                                  match.team_1,
                                )
                              }
                              disabled={
                                currentPrediction?.status === "loading" ||
                                currentPrediction?.status === "locked"
                              }
                            >
                              {currentPrediction?.status === "locked"
                                ? "Locked"
                                : "WIN"}
                            </button>
                            {teamOnePercentage ? (
                              <div className="ipl-page-win-percentage">
                                {teamOnePercentage}
                              </div>
                            ) : null}
                          </div>
                        </div>
                        <div className="ipl-page-vs-block">
                          <div className="ipl-page-vs">VS</div>
                          <div className="ipl-page-vs-line"></div>
                        </div>
                        <div
                          className="ipl-page-team-panel"
                          style={{
                            "--team-accent": teamTwoTheme.accent,
                            "--team-deep": teamTwoTheme.deep,
                            "--team-ink": teamTwoTheme.ink,
                          }}
                        >
                          {teamTwoTheme.logo ? (
                            <img
                              src={teamTwoTheme.logo}
                              alt={match.team_2}
                              className="ipl-page-team-logo"
                              onError={(event) => {
                                event.currentTarget.style.display = "none";
                                event.currentTarget.nextSibling.style.display =
                                  "flex";
                              }}
                            />
                          ) : null}
                          <div
                            className="ipl-page-team-fallback"
                            style={{
                              display: teamTwoTheme.logo ? "none" : "flex",
                            }}
                          >
                            {teamTwoTheme.shortName}
                          </div>
                          <div className="ipl-page-team-name">
                            {match.team_2}
                          </div>
                          <div className="ipl-page-team-action-row">
                            <button
                              type="button"
                              className={`ipl-page-predict-button ${
                                currentPrediction?.winnerTeam === match.team_2
                                  ? "selected"
                                  : ""
                              }`}
                              onClick={() =>
                                handlePrediction(
                                  {
                                    ...match,
                                    id: match.originalId || match.id,
                                  },
                                  match.team_2,
                                )
                              }
                              disabled={
                                currentPrediction?.status === "loading" ||
                                currentPrediction?.status === "locked"
                              }
                            >
                              {currentPrediction?.status === "locked"
                                ? "Locked"
                                : "WIN"}
                            </button>
                            {teamTwoPercentage ? (
                              <div className="ipl-page-win-percentage">
                                {teamTwoPercentage}
                              </div>
                            ) : null}
                          </div>
                        </div>
                      </div>
                      <div className="ipl-page-card-footer">
                        <div className="ipl-page-picked-chip">
                          {currentPrediction?.winnerTeam
                            ? `Your Pick: ${currentPrediction.winnerTeam}`
                            : "Your Pick: Not selected yet"}
                        </div>
                      </div>
                      {currentPrediction?.message && (
                        <div
                          className={`ipl-page-prediction-status ${currentPrediction.status}`}
                        >
                          {currentPrediction.message}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>

          <div className="ipl-page-side-column">
            <div className="ipl-page-leaderboard-wrap">
              <div className="ipl-page-leaderboard-header">
                <div>
                  <div className="ipl-page-leaderboard-kicker">Leaderboard</div>
                  <div className="ipl-page-leaderboard-title">
                    {leaderboardState.title}
                  </div>
                </div>
                <div className="ipl-page-leaderboard-badge">Top 5</div>
              </div>

              {leaderboardState.loading ? (
                <div className="ipl-page-leaderboard-empty">
                  Loading leaderboard...
                </div>
              ) : leaderboardState.topFive.length > 0 ? (
                <div className="ipl-page-leaderboard-grid">
                  {leaderboardState.topFive.map((entry, index) => {
                    const participantName =
                      entry?.user_name ||
                      entry?.name ||
                      entry?.participant_name ||
                      entry?.username ||
                      `Player ${index + 1}`;
                    const participantPoints =
                      entry?.points ??
                      entry?.score ??
                      entry?.total_points ??
                      entry?.correct_predictions ??
                      0;
                    const participantLabel =
                      entry?.client_name ||
                      entry?.team_name ||
                      entry?.company_name ||
                      "Prediction Rank";

                    return (
                      <div
                        className="ipl-page-leaderboard-card"
                        key={`${participantName}-${index}`}
                      >
                        <div className="ipl-page-leaderboard-rank">
                          #{index + 1}
                        </div>
                        <div className="ipl-page-leaderboard-name">
                          {participantName}
                        </div>
                        <div className="ipl-page-leaderboard-points">
                          {participantPoints}
                        </div>
                        <div className="ipl-page-leaderboard-label">
                          {participantLabel}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : null}
            </div>

            <div className="ipl-page-history-wrap">
              <div className="ipl-page-history-header">
                <div>Your Performance</div>
              </div>

              {userHistoryState.loading ? (
                <div>Loading history...</div>
              ) : (
                <>
                  {/* Stats */}
                  <div className="ipl-page-history-stats">
                    <div>Total: {userHistoryState.stats?.total_matches}</div>
                    <div>Wins: {userHistoryState.stats?.wins}</div>
                    <div>Losses: {userHistoryState.stats?.losses}</div>
                  </div>

                  {/* History list */}
                  <div className="ipl-page-history-list">
                    {userHistoryState.history.map((item) => (
                      <div
                        key={item.match_id}
                        className="ipl-page-history-card"
                      >
                        <div>{item.match}</div>
                        <div>{item.date}</div>
                        <div>Pick: {item.predicted_team}</div>
                        <div>Winner: {item.actual_winner}</div>
                        <div className={`status ${item.status.toLowerCase()}`}>
                          {item.status}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
